// XAxis Group Labels
import React, { ReactNode } from "react";
import { LabelList, ResponsiveContainer, Text, Tooltip } from "recharts";

import { tooltipClasses, TooltipProps } from "@mui/material";
import { styled } from "@mui/material/styles";

import Logo from "../../../../images/Ig_Logos/Logo_Horiz.png";
import theme from "../../../../theme";
import {
  BarMetricData,
  HistogramMetricData,
  StackedBarMetricData,
} from "../../../../types/analytics";

import { GraphConfig, GraphType } from "./graphConfigs";
import { getTextWidth } from "./graphHelpers";

const CustomizedAxisTick = (
  props: any,
  groupMetricData:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[],
  graphValueFormatter: (
    isAxisTick: boolean,
    value: number,
    config: GraphConfig,
    index?: number | undefined
  ) => string | undefined,
  config: GraphConfig
) => {
  const { x, y, payload } = props.props;
  const isHistogram =
    props.config && props.config.graphType === GraphType.Histogram;

  return (
    <Text
      fill={"#666666"}
      maxLines={2}
      fontSize={12}
      fontFamily={theme.typography.fontFamily}
      x={isHistogram ? x - 4.5 : x}
      angle={isHistogram ? 270 : 0}
      y={isHistogram ? y + 10 : y}
      width={
        props.config.graphType === GraphType.Line
          ? 85
          : getTextWidth(false, props.groupMetricData!.length)
      }
      textAnchor="middle"
      verticalAnchor={"start"}
    >
      {isHistogram
        ? props.graphValueFormatter(false, payload.value, props.config)
        : props.config.isLossMetric
          ? props.graphValueFormatter(false, payload.value, props.config)
          : payload.value}
    </Text>
  );
};

// Label on bar
const CustomLabel = (
  props: any,
  config: GraphConfig,
  graphValueFormatter: (
    isAxisTick: boolean,
    value: number,
    config: GraphConfig,
    index?: number | undefined
  ) => string | undefined,
  compareGroupA?: string,
  compareGroupB?: string,
  index?: number
): any => {
  const { x, y, width, value, height } = props.props;

  return (
    <text
      x={x + width / 2}
      y={y}
      dy={value > -1 ? 30 : -30}
      style={{
        fill: height <= 30 ? "none" : "white",
        fontFamily: theme.typography.fontFamily,
        fontSize:
          props.config.isImpactGraph ||
          props.config.graphType === GraphType.BiaxialBar
            ? 14
            : props.config.graphType === GraphType.ClusteredBar
              ? 13
              : 16,
      }}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {props.graphValueFormatter(false, value, props.config, props.index)}
    </text>
  );
};
// Legend
const StyledLegendText = (
  value: string,
  entry: any,
  index: number,
  formatFunction?: (string) => string
) => {
  return (
    <span
      style={{
        fontSize: "12px",
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.secondary.main,
      }}
    >
      {formatFunction ? formatFunction(value) : value}
    </span>
  );
};

const CustomResponsiveContainer = styled(ResponsiveContainer)(({ theme }) => ({
  position: "relative",
  "&:after": {
    content: '" "',
    height: "60px",
    width: "100px",
    display: "block",
    position: "absolute",
    top: 100,
    backgroundImage: `url(${Logo})`,
    backgroundSize: "100%",
    backgroundRepeat: "no-repeat",
    opacity: 0.15,
    right: "-35px",
    transform: "rotate(270deg)",
  },
}));

const headerFontSize = "15px";
const headerFontWeight = 500;

export const renderStackedBarLabelList = (
  index: number,
  valueAccessor: any,
  configCopy: any,
  graphValueFormatter: (
    isAxisTick: boolean,
    value: number,
    config: GraphConfig,
    index?: number | undefined
  ) => string | undefined
) => {
  if (configCopy.showAllStackedLabels) {
    return (
      <LabelList
        fill={"white"}
        style={{
          fontFamily: theme.typography.fontFamily,
          fontSize: "14px",
        }}
        dataKey={configCopy.stackedBarLegendTitles[index]}
        position={"center"}
        formatter={(val: any) => {
          return (val as number) > 0.05
            ? graphValueFormatter(false, val, configCopy)
            : null;
        }}
      />
    );
  } else {
    return (
      <LabelList
        style={{
          fontFamily: theme.typography.fontFamily,
          fontSize: "14px",
        }}
        position={"top"}
        valueAccessor={(val: any) =>
          valueAccessor(val, index, configCopy, graphValueFormatter)
        }
      />
    );
  }
};

export {
  CustomizedAxisTick,
  CustomLabel,
  CustomResponsiveContainer,
  headerFontSize,
  headerFontWeight,
  StyledLegendText,
};
